/**
 * Header Component
 *
 * Positioned above the game board, aligned with the board's left/right edges.
 * Left side: How to Play, Reset Game (temporary)
 * Right side: player email, Log Out
 * Renders at native CSS pixel sizes (no transform scaling) so text
 * stays readable on phones. Width matches the board's scaled width.
 */

import { useAuth } from '../hooks/useAuth';
import { useBoardLayout, BOARD_WIDTH, HEADER_BAR_HEIGHT } from '../hooks/useBoardLayout';
import styles from './Header.module.css';

interface HeaderProps {
  /** Optional reset callback — RESET button only shows when provided */
  onReset?: () => void;
  /** Logout callback — handled by App to coordinate state reset */
  onLogout?: () => void;
}

function Header({ onReset, onLogout }: HeaderProps) {
  const { user } = useAuth();
  const { scale, left, headerTop } = useBoardLayout();

  if (!user) return null;

  const handleReset = onReset ? () => {
    if (confirm('Reset game and clear all progress?')) {
      onReset();
    }
  } : undefined;

  // Scale header with the board, but enforce a minimum so it stays readable on mobile.
  // Below scale 0.7, boost proportionally so the visual size matches scale=0.7.
  const headerScale = scale < 0.7 ? 0.7 : scale;
  const headerFontSize = Math.min(13, 13 * headerScale);
  const headerHeight = HEADER_BAR_HEIGHT * headerScale;

  return (
    <div
      style={{
        position: 'fixed',
        left,
        top: headerTop,
        width: BOARD_WIDTH * scale,
        height: headerHeight,
        fontSize: headerFontSize,
        zIndex: 3,
      }}
      className={styles.header}
    >
      <div className={styles.leftGroup}>
        <button className={styles.headerButton} onClick={() => {}}>
          How to Play
        </button>
        {handleReset && (
          <button className={styles.resetButton} onClick={handleReset}>
            RESET
          </button>
        )}
      </div>
      <div className={styles.rightGroup}>
        <span className={styles.userEmail}>{user.email}</span>
        <span className={styles.userIcon} title={user.email}>
          <svg viewBox="0 0 24 24" width="20" height="20" fill="#f7e5c5">
            <circle cx="12" cy="8" r="4" />
            <path d="M4 20c0-4.4 3.6-8 8-8s8 3.6 8 8" />
          </svg>
        </span>
        <button className={styles.headerButton} onClick={() => { console.log('[Header] onLogout:', typeof onLogout); onLogout?.(); }}>
          Log Out
        </button>
      </div>
    </div>
  );
}

export default Header;
